import { ApplicationElement, html } from '@effectivastudio/lit-components';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import translations from './translations.js';

import style from './style.lit.css';

import '@effectivastudio/lit-components/components/img_placeholder/element.js';
import '@effectivastudio/lit-components/components/badge/element.js';
import '@effectivastudio/lit-components/components/icon/element.js';
import '../origin/element.js';

/**
 * Component that links to a group or a group-joining page, with details about the group.
 *
 * @prop favoriteOn - Adds or removes option of marking the group as favorite (optional)
 * @prop favorite - Is group among current users favorites (optional)
 * @prop url - The whole element will link to this URL (optional)
 * @prop image - Group's avatar image (optional)
 * @prop membersUrl - Url to group members page (optional)
 * @prop membersCount - Number of members
 * @prop theme - Component background (options: primary, white, gray)
 * @prop notificationCount - Badge value, number of group related notifications (optional)
 * @prop type - Type of the current group (options: open, private, closed)
 * @prop region - region name for the origin component
 * @prop regionImage - region image for the origin component
 * @prop regionUrl - region url for the origin component
 * @prop regionIcon - region icon for the origin component if region image is not defined
 * @prop name - Internal property => Will be derived from the innerText of the element and will be passed as alt text for image in image placeholder
 *
 * @cssprop --padding - Component's padding on mobile
 * @cssprop --background - Component's background
 * @cssprop --background-hover - Component's hover background color
 * @cssprop --background-active - Component's active background color
 * @cssprop --color-primary - Group name and additional title text color
 * @cssprop --color-secondary - Component's additional info text color
 *
 * @slot main - For the group name
 * @slot top - For additional info
 */

export default class GroupItem2 extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    favoriteOn: { type: Boolean },
    favorite: { type: Boolean },
    url: { type: String },
    image: { type: String },
    membersUrl: { type: String },
    membersCount: { type: Number },
    notificationCount: { type: Number },
    theme: { type: String },
    type: { type: String },
    region: { type: String },
    regionUrl: { type: String },
    regionIcon: { type: String },
    regionImage: { type: String },
    name: { state: true },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  static TYPE_ICON_NAMES = {
    closed: 'lock-keyhole',
    private: 'user-lock',
    open: 'unlock-keyhole',
  };

  /** @internal */
  get infoOn() {
    return this.type || this.membersCount || this.region;
  }

  /** @internal */
  get typeIcon() {
    return this.constructor.TYPE_ICON_NAMES[this.type];
  }

  /** @internal */
  get themeClass() {
    return {
      gray: this.theme === 'gray',
      primary: this.theme === 'primary',
      white: this.theme === 'white',
    };
  }

  onSlotChange() {
    this.name = this.innerText;
  }

  renderFavorite() {
    return this.favorite
      ? html`<ef-icon name="star:solid"></ef-icon>`
      : html`<ef-icon name="star"></ef-icon>`;
  }

  renderBadge() {
    return html`
      <ef-badge count=${this.notificationCount} theme="danger"></ef-badge>
    `;
  }

  renderTypeIcon() {
    return html`<ef-icon class="type" name=${this.typeIcon}></ef-icon>`;
  }

  renderMemberCount() {
    return !this.url && this.membersUrl
      ? html`
          <a href=${this.membersUrl} class="members">
            ${this.t(`members_count`, { count: this.membersCount })}
          </a>
        `
      : html`
          <span class="members">
            ${this.t(`members_count`, { count: this.membersCount })}
          </span>
        `;
  }

  renderOrigin() {
    return html`
      <xs-origin
        .name=${this.region}
        .picture=${this.regionImage}
        .url=${this.url ? null : this.regionUrl}
        .icon=${this.regionIcon}>
        ${this.region}
      </xs-origin>
    `;
  }

  renderInfo() {
    return html`
      <div class="info">
        ${this.type ? html`${this.renderTypeIcon()}` : null}
        ${this.membersCount ? html`${this.renderMemberCount()}` : null}
        ${this.region ? html`${this.renderOrigin()}` : null}
      </div>
    `;
  }

  renderContent() {
    return html`
      <div class="wrapper">
        <ef-img-placeholder
          picture=${ifDefined(this.image)}
          alt=${ifDefined(this.name)}>
        </ef-img-placeholder>

        <div class="content">
          <slot name="top"></slot>
          <slot name="main" @slotchange=${this.onSlotChange}></slot>
          ${this.infoOn ? this.renderInfo() : null}
        </div>

        <aside>
          ${this.favoriteOn ? this.renderFavorite() : null}
          ${this.notificationCount ? this.renderBadge() : null}
        </aside>
      </div>
    `;
  }

  renderContentWithLink() {
    return html`<a href=${this.url}> ${this.renderContent()}</a> `;
  }

  render() {
    return html`
      <main class=${classMap(this.themeClass)}>
        ${this.url ? this.renderContentWithLink() : this.renderContent()}
      </main>
    `;
  }
}

GroupItem2.registerTagName('xs-group-item-2');

customElements.get('xs-group-item-2') ||
  customElements.define('xs-group-item-2', GroupItem2);
